import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { sanitizeBrandNameForUrl } from '../../utils/brandUrlHelpers'
import { supabase } from '../../lib/supabase'

interface Brand {
  name: string
  productCount: number
}

export function BrandsDirectory() {
  const navigate = useNavigate()
  const [brands, setBrands] = useState<Brand[]>([])
  const [filteredBrands, setFilteredBrands] = useState<Brand[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    fetchBrands()
  }, [])

  useEffect(() => {
    if (searchQuery) {
      const filtered = brands.filter(brand => 
        brand.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
      setFilteredBrands(filtered)
    } else {
      setFilteredBrands(brands)
    }
  }, [searchQuery, brands])

  const fetchBrands = async () => {
    try {
      setIsLoading(true)

      // Query brands table and count contributions
      const { data: brandsData, error } = await supabase
        .from('brands')
        .select(`
          name,
          slug,
          contributions:contributions(count)
        `)
        .order('name')

      if (error) {
        console.error('Error fetching brands:', error)
        return
      }

      // Transform the data and filter out brands with no products
      const brandsList = (brandsData || [])
        .map(brand => ({
          name: brand.name,
          productCount: brand.contributions?.[0]?.count || 0
        }))
        .filter(brand => brand.productCount > 0) // Only show brands with products
        .sort((a, b) => a.name.localeCompare(b.name))

      setBrands(brandsList)
      setFilteredBrands(brandsList)
    } catch (err) {
      console.error('Error fetching brands:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // Group brands by first letter
  const groupedBrands = filteredBrands.reduce((acc, brand) => {
    const firstLetter = brand.name.charAt(0).toUpperCase()
    if (!acc[firstLetter]) {
      acc[firstLetter] = []
    }
    acc[firstLetter].push(brand)
    return acc
  }, {} as Record<string, Brand[]>)

  const handleBrandClick = (brandName: string) => {
    const brandUrl = `/brand/${sanitizeBrandNameForUrl(brandName)}`
    console.log('Navigating to brand:', brandName, '-> URL:', brandUrl)
    navigate(brandUrl)
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen">
        <div className="w-full max-w-7xl mx-auto px-6 py-12">
          <div className="animate-pulse">
            <div className="h-10 bg-gray-200 rounded w-32 mb-8"></div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {Array.from({ length: 20 }).map((_, i) => (
                <div key={i} className="h-5 bg-gray-100 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen">
      {/* Left Sidebar with Search */}
      <div className="hidden lg:block w-80 flex-shrink-0 border-r border-gray-200">
        <div className="sticky top-20 p-8">
          <h2 className="text-lg mb-6" style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif", fontWeight: 500 }}>
            Search
          </h2>
          
          {/* Search Input */}
          <div className="relative mb-8">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-6 py-2 border-b border-gray-300 focus:border-gray-900 transition-colors outline-none bg-transparent"
              style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}
            />
          </div>

          {/* Alphabet Jump Links */}
          <div className="space-y-1">
            <h3 className="text-sm text-gray-500 mb-3" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
              JUMP TO
            </h3>
            <div className="flex flex-wrap gap-1">
              {Object.keys(groupedBrands).sort().map(letter => (
                <button
                  key={letter}
                  onClick={(e) => {
                    e.preventDefault()
                    const element = document.getElementById(`letter-${letter}`)
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  }}
                  className="w-8 h-8 flex items-center justify-center text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer"
                  style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
              {filteredBrands.length} {filteredBrands.length === 1 ? 'brand' : 'brands'}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 px-6 lg:px-12 py-12">
        {/* Mobile Search - visible only on mobile */}
        <div className="lg:hidden mb-8">
          <div className="relative">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-6 py-2 border-b border-gray-300 focus:border-gray-900 transition-colors outline-none bg-transparent"
              style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}
            />
          </div>
        </div>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-light mb-2" style={{ fontFamily: 'Canela, Georgia, serif', fontWeight: 300 }}>
            Brands
          </h1>
          <p className="text-sm text-gray-600" style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}>
            Discover brands shared by our community
          </p>
        </div>

        {/* Brands Grid */}
        {filteredBrands.length > 0 ? (
          <div className="space-y-16">
            {Object.entries(groupedBrands).sort().map(([letter, letterBrands]) => (
              <div key={letter} id={`letter-${letter}`} className="scroll-mt-24">
                {/* Letter Header */}
                <div className="mb-6 pb-3 border-b border-gray-200">
                  <h2 className="text-2xl" style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif", fontWeight: 300 }}>
                    {letter}
                  </h2>
                </div>

                {/* Brands Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-4">
                  {letterBrands.map(brand => (
                    <button
                      key={brand.name}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleBrandClick(brand.name)
                      }}
                      className="text-left group py-2"
                    >
                      <span 
                        className="text-base text-gray-900 group-hover:underline transition-colors inline-block"
                        style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif", fontWeight: 400 }}
                      >
                        {brand.name}
                      </span>
                      <span 
                        className="text-sm text-gray-400 ml-2"
                        style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                      >
                        ({brand.productCount})
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <p className="text-gray-500" style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}>
              {searchQuery ? `No brands found matching "${searchQuery}"` : 'No brands found'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}